import { useLocation } from "@reach/router"
import get from "lodash/get"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import type { LinkPropertiesHook } from "~/hooks/useLinkProperties"
import useLinkPropertiesCollection from "~/hooks/useLinkPropertiesCollection"

/**
 * Returns link props for each product's detail page.
 *
 * Resolves `flatData.pages[0].flatData.url` for every product and builds
 * `{ href, onClick }` objects suitable for the UDS Link component.
 *
 * Returns `null` for products that have no page URL or whose page URL matches
 * the current pathname (to avoid linking to the page the user is already on).
 *
 * @param products - List of comparable products to resolve links for.
 *
 * @returns Array of `LinkPropertiesHook` (or `null`) in the same order as `products`.
 */
export default function useProductDetailPageLinks(
  products: ComparableProduct[]
): (LinkPropertiesHook | null)[] {
  const pageUrls = products.map(
    (product) =>
      get(product, "flatData.pages[0].flatData.url") as string | undefined
  )

  const resolvedLinks = useLinkPropertiesCollection(
    pageUrls.filter((pageUrl): pageUrl is string => !!pageUrl)
  )

  const { pathname: currentPathname } = useLocation() as Window["location"]

  return pageUrls.map((url) =>
    url && currentPathname !== url ? (resolvedLinks[url] ?? null) : null
  )
}
